/**
 * @file     MeterRow.tsx
 * @purpose  Hub meter row: label, an 8px pill track with a tone fill, and a
 *           right-aligned value line, on a hairline. Exposed as an ARIA meter
 *           whose value text is the visible line, so the reading never depends
 *           on the bar's colour or length. Stacks into a list on its own.
 */
import Box from '@mui/material/Box';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { hairline, hubColors } from './hubKitStyles';

export type MeterTone = 'positive' | 'neutral' | 'negative' | 'primary';

export interface MeterRowProps {
  label: string;
  /** Position on the track, 0 to `max`. Clamped. */
  value: number;
  max?: number;
  /** The visible reading, e.g. "6% below similar listings". */
  valueText: string;
  tone?: MeterTone;
}

const toneFill: Record<MeterTone, string> = {
  positive: hubColors.statusSuccess,
  neutral: hubColors.textSecondary,
  negative: hubColors.statusError,
  primary: hubColors.interactivePrimary,
};

export function MeterRow({ label, value, max = 100, valueText, tone = 'neutral' }: MeterRowProps) {
  const safeMax = max > 0 ? max : 100;
  const clamped = Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), safeMax);
  const percent = (clamped / safeMax) * 100;
  return (
    <Box
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      aria-valuenow={clamped}
      aria-valuetext={valueText}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(120px, 1fr) minmax(120px, 1.4fr) minmax(0, 1.2fr)' },
        gridTemplateAreas: { xs: '"label value" "track track"', sm: '"label track value"' },
        alignItems: 'center',
        columnGap: { xs: 3, sm: 6 },
        rowGap: 2,
        minHeight: 52,
        py: 3,
        borderBottom: hairline,
      }}
    >
      <Box component="span" sx={{ gridArea: 'label', fontSize: 15, fontWeight: fontWeightTokens.medium, lineHeight: 1.4, color: hubColors.textPrimary, minWidth: 0 }}>
        {label}
      </Box>
      <Box
        aria-hidden
        sx={{
          gridArea: 'track',
          position: 'relative',
          height: 8,
          borderRadius: `${layoutTokens.radius.pill}px`,
          backgroundColor: hubColors.surfaceSunken,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${percent}%`,
            borderRadius: 'inherit',
            backgroundColor: toneFill[tone],
          }}
        />
      </Box>
      <Box
        component="span"
        sx={{
          gridArea: 'value',
          fontSize: 14,
          lineHeight: 1.45,
          color: hubColors.textSecondary,
          textAlign: 'end',
          fontVariantNumeric: 'tabular-nums',
          minWidth: 0,
        }}
      >
        {valueText}
      </Box>
    </Box>
  );
}
